import classNames from "classnames";
import { useRouter } from "next/router";

import { useBrand } from "components/Context/BrandContext";
import SectionWithPromo from "components/SectionWithPromo/SectionWithPromo";
import { useTranslation } from "lib/i18n/client";
import { promotionForAsset } from "lib/promotion/promotion";

import AssetHomeCardSpace from "./AssetHomeCardSpace/AssetHomeCardSpace";
import {
  AssetSectionSpaceCategoriesAvailabilityQuery,
  AssetSectionSpaceCategoriesSpaceCategoryFragment,
  useAssetSectionSpaceCategoriesAvailabilityQuery,
} from "./AssetSectionSpaceCategories.generated";
import styles from "./AssetSectionSpaceCategories.module.css";

const AssetSectionSpaceCategories: React.FC<
  AssetSectionSpaceCategoriesProps
> = ({ title, asset, assetGQL, isViewerInCommunity, onContactClicked }) => {
  const { t } = useTranslation("asset");
  const brand = useBrand();
  const { query } = useRouter();
  const { from, to, voucher } = query;

  const {
    data,
    isLoading: isLoadingGQL,
    isFetching: isFetchingGQL,
  } = useAssetSectionSpaceCategoriesAvailabilityQuery(
    {
      assetId: assetGQL?.id || "",
      checkinDate: from as string,
      checkoutDate: to as string,
      voucherCode: voucher as string,
    },
    { enabled: !!from && !!to },
  );
  const isLoading = isLoadingGQL && isFetchingGQL;

  const alternativeDestination =
    asset.destination.destination &&
    (asset.destination.destination.assets || []).length > 1
      ? {
          title: asset.destination.destination.title,
          uri: `${asset.destination.destination.uri}${
            query.from && query.to
              ? `?${new URLSearchParams({
                  from: (query.from || "") as string,
                  to: (query.to || "") as string,
                  voucher: (query.voucher || "") as string,
                }).toString()}#assets`
              : ""
          }`,
        }
      : undefined;

  return (
    <SectionWithPromo
      background="contrast"
      id="spaces"
      title={t("spacesTitle", { city: asset.properties.location.city })}
      label={title}
      brand={brand}
      promotion={promotionForAsset(asset.properties.id, brand)}
    >
      <ul className={classNames(styles.spacesGrid, "grid")}>
        {(assetGQL?.spaceCategories || [])
          .sort((spaceA, spaceB) => {
            const hasAvailabilityA =
              (
                data?.asset?.spaceCategories?.find(
                  (cat) => cat.id === spaceA.id,
                )?.availableRates?.nodes || []
              ).length > 0;
            const hasAvailabilityB =
              (
                data?.asset?.spaceCategories?.find(
                  (cat) => cat.id === spaceB.id,
                )?.availableRates?.nodes || []
              ).length > 0;

            if (hasAvailabilityA === hasAvailabilityB) {
              return spaceA.position - spaceB.position;
            }
            return hasAvailabilityA ? -1 : 1;
          })
          .map((spaceCategory) => {
            const minRate = findCheapestSpaceCategoryRate(
              data,
              spaceCategory.id,
            );

            return (
              <li className={styles.spacesItem} key={spaceCategory.id}>
                <AssetHomeCardSpace
                  spaceCategory={spaceCategory}
                  onContactClicked={onContactClicked}
                  rate={minRate}
                  isRateLoading={isLoading}
                  isViewerInCommunity={isViewerInCommunity}
                  alternativeDestination={alternativeDestination}
                  asset={asset}
                  voucher={query.voucher as string}
                />
              </li>
            );
          })}
      </ul>
    </SectionWithPromo>
  );
};

const findCheapestSpaceCategoryRate = (
  data: AssetSectionSpaceCategoriesAvailabilityQuery | undefined,
  spaceCategoryId: string,
) => {
  if (
    !data?.asset?.spaceCategories ||
    data.asset.spaceCategories.length === 0
  ) {
    return null;
  }

  const sc = data.asset.spaceCategories.find(
    (category) => category.id === spaceCategoryId,
  );
  if (!sc?.availableRates?.nodes || sc.availableRates.nodes.length === 0) {
    return null;
  }

  return sc.availableRates.nodes.sort((a, b) => {
    return a.perNightPrice.amount < b.perNightPrice.amount ? -1 : 1;
  })[0];
};

type AssetSectionSpaceCategoriesProps = {
  title: string;
  isViewerInCommunity: boolean;
  asset: {
    properties: {
      id: string;
      stayType: string;
      closed: boolean;
      type: string;
      location: {
        city: string;
      };
    };
    destination: {
      destination?: {
        title: string;
        uri: string;
        assets: Array<{ title: string; uri: string }>;
      };
    };
  };
  assetGQL:
    | {
        id: string;
        spaceCategories:
          | AssetSectionSpaceCategoriesSpaceCategoryFragment[]
          | null
          | undefined;
      }
    | null
    | undefined;
  onContactClicked: () => void;
};

export default AssetSectionSpaceCategories;
